import { useEffect, useState } from 'react';
import { api } from '../../client/api';
import { ContentSkeleton } from '../../components/skeleton/ContentSkeleton';
import type { BoTenant, Plan, PlanInput } from '../../client/api';
import { cn } from '../../utils/cn';
import {
  BoPage,
  Section,
  ErrorBanner,
  OkBanner,
  btnGhost,
  btnPrimary,
  errMsg,
  formatBytesLimit,
  formatCountLimit,
  inputCls,
} from './ui';

// =============================================================================
// BO · Plans (NBL_REQ_804 / HNBL-109) — plan catalog CRUD + org→plan assignment.
// =============================================================================

const GB = 1024 * 1024 * 1024;

/** Empty string → null (unlimited); otherwise parse GB → bytes. */
function gbToBytes(v: string): number | null {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * GB) : null;
}
function countOrNull(v: string): number | null {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
}

type PlanFormState = {
  name: string;
  description: string;
  storageGb: string;
  deviceLimit: string;
  userLimit: string;
};

const emptyForm: PlanFormState = {
  name: '',
  description: '',
  storageGb: '',
  deviceLimit: '',
  userLimit: '',
};

function PlanCatalog({
  plans,
  onChanged,
  setErr,
}: {
  plans: Plan[];
  onChanged: () => Promise<void>;
  setErr: (m: string | null) => void;
}): JSX.Element {
  const [form, setForm] = useState<PlanFormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function set<K extends keyof PlanFormState>(k: K, v: string) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function startEdit(p: Plan) {
    setEditingId(p.id);
    setForm({
      name: p.name,
      description: p.description ?? '',
      storageGb: p.storageBytesLimit === null ? '' : String(p.storageBytesLimit / GB),
      deviceLimit: p.deviceLimit === null ? '' : String(p.deviceLimit),
      userLimit: p.userLimit === null ? '' : String(p.userLimit),
    });
  }

  function reset() {
    setEditingId(null);
    setForm(emptyForm);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setErr(null);
    const payload: PlanInput = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      storageBytesLimit: gbToBytes(form.storageGb),
      deviceLimit: countOrNull(form.deviceLimit),
      userLimit: countOrNull(form.userLimit),
    };
    try {
      if (editingId) await api.boUpdatePlan(editingId, payload);
      else await api.boCreatePlan(payload);
      reset();
      await onChanged();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(p: Plan) {
    if (!confirm(`플랜 '${p.name}'을 삭제하시겠습니까?`)) return;
    setErr(null);
    try {
      await api.boDeletePlan(p.id);
      if (editingId === p.id) reset();
      await onChanged();
    } catch (e) {
      setErr(errMsg(e));
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-2">
        <Field label="플랜명 *" w="w-40">
          <input className={cn(inputCls, 'w-40')} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Pro" />
        </Field>
        <Field label="설명" w="w-48">
          <input className={cn(inputCls, 'w-48')} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="중소 규모 팀" />
        </Field>
        <Field label="스토리지(GB)" w="w-28">
          <input className={cn(inputCls, 'w-28')} value={form.storageGb} onChange={(e) => set('storageGb', e.target.value)} placeholder="∞" inputMode="decimal" />
        </Field>
        <Field label="디바이스" w="w-24">
          <input className={cn(inputCls, 'w-24')} value={form.deviceLimit} onChange={(e) => set('deviceLimit', e.target.value)} placeholder="∞" inputMode="numeric" />
        </Field>
        <Field label="사용자" w="w-24">
          <input className={cn(inputCls, 'w-24')} value={form.userLimit} onChange={(e) => set('userLimit', e.target.value)} placeholder="∞" inputMode="numeric" />
        </Field>
        <button type="submit" disabled={saving || !form.name.trim()} className={btnPrimary}>
          {saving ? '저장 중…' : editingId ? '플랜 수정' : '플랜 추가'}
        </button>
        {editingId && (
          <button type="button" className={btnGhost} onClick={reset}>
            취소
          </button>
        )}
      </form>
      <p className="text-[13px] text-text-faint">빈 칸은 무제한(∞)으로 저장됩니다.</p>

      {plans.length === 0 ? (
        <p className="py-6 text-center text-[14px] text-text-faint">플랜이 없습니다.</p>
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-[14px]">
            <thead>
              <tr className="border-b border-line bg-panel-2">
                <Th>플랜</Th>
                <Th>스토리지</Th>
                <Th>디바이스</Th>
                <Th>사용자</Th>
                <Th right></Th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id} className={cn('border-b border-line last:border-0 hover:bg-panel-hover transition-colors', editingId === p.id && 'bg-accent-soft/40')}>
                  <td className="px-3 py-2">
                    <div className="text-text">{p.name}</div>
                    {p.description && <div className="text-[13px] text-text-dim">{p.description}</div>}
                  </td>
                  <td className="px-3 py-2 text-text-dim">{formatBytesLimit(p.storageBytesLimit)}</td>
                  <td className="px-3 py-2 text-text-dim">{formatCountLimit(p.deviceLimit)}</td>
                  <td className="px-3 py-2 text-text-dim">{formatCountLimit(p.userLimit)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1.5">
                      <button type="button" className={btnGhost} onClick={() => startEdit(p)}>수정</button>
                      <button type="button" className={cn(btnGhost, 'text-warning')} onClick={() => void handleDelete(p)}>삭제</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OrgAssignment({
  plans,
  loadingPlans,
  setErr,
}: {
  plans: Plan[];
  loadingPlans: boolean;
  setErr: (m: string | null) => void;
}): JSX.Element {
  const [tenants, setTenants] = useState<BoTenant[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [orgId, setOrgId] = useState('');
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [planId, setPlanId] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setTenants(await api.boListTenants());
      } catch (e) {
        setErr(errMsg(e));
      } finally {
        setLoadingTenants(false);
      }
    })();
  }, [setErr]);

  async function selectOrg(id: string) {
    setOrgId(id);
    setMsg(null);
    setCurrentPlanId(null);
    setPlanId('');
    if (!id) return;
    try {
      const view = await api.boGetOrgPlan(id);
      setCurrentPlanId(view.plan?.id ?? null);
      setPlanId(view.plan?.id ?? '');
    } catch (e) {
      setErr(errMsg(e));
    }
  }

  async function handleAssign() {
    if (!orgId || !planId) return;
    setBusy(true);
    setErr(null);
    try {
      const view = await api.boAssignPlan(orgId, planId);
      setCurrentPlanId(view.plan?.id ?? null);
      setMsg(`플랜을 '${view.plan?.name ?? planId}'(으)로 할당했습니다.`);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleUnassign() {
    if (!orgId) return;
    setBusy(true);
    setErr(null);
    try {
      await api.boUnassignPlan(orgId);
      setCurrentPlanId(null);
      setPlanId('');
      setMsg('플랜 할당을 해제했습니다.');
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  const currentPlanName = plans.find((p) => p.id === currentPlanId)?.name ?? null;

  if (loadingPlans || loadingTenants) return <ContentSkeleton label="조직 플랜 할당 정보를 불러오는 중…" variant="form" />;

  return (
    <div className="flex flex-col gap-3">
      <OkBanner message={msg} />
      <div className="flex flex-wrap items-end gap-2">
        <Field label="조직" w="w-56">
          <select className={cn(inputCls, 'w-56')} value={orgId} onChange={(e) => void selectOrg(e.target.value)}>
            <option value="">조직 선택…</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </Field>
        <Field label="플랜" w="w-48">
          <select className={cn(inputCls, 'w-48')} value={planId} onChange={(e) => setPlanId(e.target.value)} disabled={!orgId}>
            <option value="">플랜 선택…</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </Field>
        <button type="button" className={btnPrimary} disabled={busy || !orgId || !planId} onClick={() => void handleAssign()}>
          할당
        </button>
        <button type="button" className={btnGhost} disabled={busy || !orgId || !currentPlanId} onClick={() => void handleUnassign()}>
          할당 해제
        </button>
      </div>
      {orgId && (
        <p className="text-[14px] text-text-dim">
          현재 할당: {currentPlanName ? <span className="text-text">{currentPlanName}</span> : <span className="text-text-faint">없음(무제한)</span>}
        </p>
      )}
    </div>
  );
}

function Field({ label, w, children }: { label: string; w: string; children: React.ReactNode }) {
  return (
    <div className={cn('flex flex-col gap-1', w)}>
      <label className="text-[13px] font-medium text-text-dim">{label}</label>
      {children}
    </div>
  );
}

function Th({ children, right }: { children?: React.ReactNode; right?: boolean }) {
  return <th className={cn('px-3 py-2 font-semibold text-text-dim', right ? 'text-right' : 'text-left')}>{children}</th>;
}

export function Plans(): JSX.Element {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try {
      setPlans(await api.boListPlans());
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  return (
    <BoPage title="플랜 관리" subtitle="운영사 전용 — 플랜 카탈로그 정의와 조직별 플랜 할당.">
      <ErrorBanner message={err} />
      <Section title="플랜 카탈로그">
        {loading ? (
          <ContentSkeleton label="플랜 카탈로그를 불러오는 중…" variant="split" />
        ) : (
          <PlanCatalog plans={plans} onChanged={load} setErr={setErr} />
        )}
      </Section>
      <Section title="조직 플랜 할당">
        <OrgAssignment plans={plans} loadingPlans={loading} setErr={setErr} />
      </Section>
    </BoPage>
  );
}
